import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/common/Answer';
import { ExportButton } from '@/components/common/ExportButton';
import { codedText } from '@/lib/codings';
import type { AnswerOut, CadeCaseCodings, CadeCodedField, CadeCodingQuote } from '@/services/types';

/** The refusal that is about the CORPUS (the table is not loaded here), not this case. */
const NO_TABLE = 'substrate.reasons.cadeCodingsNoTable';

/**
 * What the case's votes say, field by field: conduct and theory of harm, the relevant market,
 * the fine's base and circumstances, remedies, TCC terms, and the SG / ProCADE / MPF opinions.
 *
 * Only SERVED values: two local models read the same thing and every quote is an exact substring
 * of the case material. Each value shows the quote it rests on and the SEI document the quote
 * was found in; withheld fields are counted by reason and never shown. A value is the vote as
 * read -- never a finding of this product, and never one party's result.
 */
export function CodingsPanel({
  caseKey,
  codings,
}: {
  caseKey: string;
  codings: AnswerOut<CadeCaseCodings>;
}) {
  const { t, i18n } = useTranslation(['cade']);
  const scope = codings.reason_key === NO_TABLE ? 'corpus' : 'record';
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-codings>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{t('cade:codings.title')}</h2>
        {codings.value ? (
          <ExportButton path={`/api/v1/cade/export/c/${encodeURIComponent(caseKey)}/coded`} />
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">{t('cade:codings.help')}</p>
      <Answer answer={codings} scope={scope} help={t('cade:codings.help')}>
        {(value: CadeCaseCodings) => (
          <div className="space-y-4 text-sm">
            {value.served.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('cade:codings.noneServed')}</p>
            ) : (
              <Groups fields={value.served} />
            )}
            <Footer value={value} percent={percent} />
          </div>
        )}
      </Answer>
    </section>
  );
}

function Groups({ fields }: { fields: CadeCodedField[] }) {
  const { t } = useTranslation(['cade']);
  // Server order is the schema's order; groups are taken in the order they first appear.
  const groups: [string, CadeCodedField[]][] = [];
  for (const field of fields) {
    const key = field.group ?? 'other';
    const last = groups.at(-1);
    if (last && last[0] === key) last[1].push(field);
    else groups.push([key, [field]]);
  }
  return (
    <>
      {groups.map(([group, items]) => (
        <div key={group} data-coding-group={group}>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t(`cade:codings.groups.${group}`, { defaultValue: group })}
          </h3>
          <dl className="space-y-2">
            {items.map((field) => (
              <Field key={field.field} field={field} />
            ))}
          </dl>
        </div>
      ))}
    </>
  );
}

function Field({ field }: { field: CadeCodedField }) {
  const { t, i18n } = useTranslation(['cade']);
  return (
    <div data-coded-field={field.field}>
      <dt className="text-xs text-muted-foreground">
        {t(`cade:codings.fields.${field.field}`, { defaultValue: field.field })}
      </dt>
      <dd className="text-foreground">
        {codedText(field, t, i18n.language)}
        {/* Inside the <dd>: a <details> beside the dt/dd pair breaks the definition list. */}
        {field.quotes.length > 0 ? (
          <details className="mt-1 text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              {t('cade:codings.quote', { count: field.quotes.length })}
            </summary>
            <ul className="mt-1 space-y-2">
              {field.quotes.map((quote, i) => (
                <QuoteItem key={i} quote={quote} />
              ))}
            </ul>
          </details>
        ) : null}
      </dd>
    </div>
  );
}

function QuoteItem({ quote }: { quote: CadeCodingQuote }) {
  const { t, i18n } = useTranslation(['cade']);
  const day = new Intl.DateTimeFormat(i18n.language, {
    dateStyle: 'medium',
    timeZone: 'UTC',
  });
  return (
    <li>
      <blockquote className="whitespace-pre-line border-l-2 pl-3 text-muted-foreground">
        {quote.text}
      </blockquote>
      <p className="pl-3 text-muted-foreground">
        {quote.doc_type
          ? t('cade:codings.source', {
              type: quote.doc_type,
              date: quote.doc_date ? day.format(new Date(`${quote.doc_date}T00:00:00Z`)) : '—',
            })
          : t('cade:codings.sourceUnknown')}
        {quote.url ? (
          <a
            href={quote.url}
            target="_blank"
            rel="noreferrer"
            className="ml-2 inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
          >
            {t('cade:codings.openSei')}
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        ) : null}
      </p>
    </li>
  );
}

function Footer({ value, percent }: { value: CadeCaseCodings; percent: Intl.NumberFormat }) {
  const { t } = useTranslation(['cade']);
  const withheld = Object.entries(value.under_review);
  const withheldTotal = withheld.reduce((sum, [, n]) => sum + n, 0);
  return (
    <div className="space-y-1 border-t pt-2 text-xs text-muted-foreground" data-codings-footer>
      {value.not_stated.length > 0 ? (
        <p>
          {t('cade:codings.notStated', {
            fields: value.not_stated
              .map((f) => t(`cade:codings.fields.${f}`, { defaultValue: f }))
              .join(', '),
          })}
        </p>
      ) : null}
      {withheldTotal > 0 ? (
        <p data-codings-withheld>
          {t('cade:codings.underReview', {
            count: withheldTotal,
            reasons: withheld
              .map(
                ([reason, n]) =>
                  `${t(`cade:codings.reasons.${reason}`, { defaultValue: reason })} (${n})`,
              )
              .join(', '),
          })}
        </p>
      ) : null}
      <p>
        {t('cade:codings.agreement', {
          cases: value.reference_cases,
          share: percent.format(value.reference_agreement),
          served: value.reference_served,
        })}
      </p>
      <p>
        {t('cade:codings.model', {
          main: value.main_model,
          check: value.check_model ?? '—',
          prompt: value.prompt_version,
          schema: value.schema_version,
        })}
      </p>
    </div>
  );
}
